import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import useShippingRequest from '../../hooks/useShippingRequest';
import distributorService from '../../services/distributorService';
import './CashBank.css';

const integer = (value) => Number.isSafeInteger(value) && value >= 0;
const adminAccess = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return (
      user?.role === 'admin' &&
      user.isActive !== false &&
      !user.disabled &&
      !user.distributorPreview &&
      !user.distributorPreviewExpiresAt
    );
  } catch {
    return false;
  }
};
const read = (response, id) => {
  const row = response?.data?.data;
  if (
    !row ||
    row.schemaVersion !== 1 ||
    row.distributorId !== id ||
    !['postingRevision', 'balanceCents', 'reviewCount'].every((key) => integer(row[key])) ||
    !['available', 'blocked', 'manualDisputePaused'].every(
      (key) => typeof row[key] === 'boolean'
    ) ||
    (row.available && (!row.blocked || row.manualDisputePaused || row.reviewCount < 1))
  )
    throw new Error('Invalid funding review status');
  return row;
};
const readRecovery = (response, current) => {
  const row = response?.data?.data;
  if (
    !row ||
    row.schemaVersion !== 1 ||
    row.distributorId !== current.distributorId ||
    row.recovered !== true ||
    !integer(row.postingRevision) ||
    row.postingRevision <= current.postingRevision ||
    row.balanceCents !== current.balanceCents ||
    !integer(row.resolvedCount) ||
    row.resolvedCount < 1 ||
    !Number.isFinite(Date.parse(row.fundingReviewValidUntil))
  )
    throw new Error('Unconfirmed recovery');
  return row;
};

export default function CashFundingRecovery({
  distributorId,
  disabled = false,
  onLockChange,
  onUpdated,
}) {
  const t = useT();
  const allowed = adminAccess() && /^[a-f\d]{24}$/.test(distributorId || '');
  const [state, setState] = useState(null),
    [loadedVersion, setLoadedVersion] = useState('');
  const [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [reason, setReason] = useState(''),
    [retry, setRetry] = useState(0);
  const saving = useRef(false),
    pending = useRef(null);
  const callbacks = useRef({ onLockChange, onUpdated });
  callbacks.current = { onLockChange, onUpdated };
  const { begin, version } = useShippingRequest(`cash-funding-recovery:${distributorId}`);
  useEffect(() => {
    callbacks.current.onLockChange?.(busy || uncertain);
    return () => callbacks.current.onLockChange?.(false);
  }, [busy, uncertain]);
  useEffect(() => {
    pending.current = null;
    saving.current = false;
    setUncertain(false);
    setReason('');
    setNotice('');
  }, [distributorId, version]);
  useEffect(() => {
    const request = begin();
    setState(null);
    setError('');
    setBusy(allowed);
    if (!allowed) return;
    Promise.resolve(
      distributorService.adminCashFundingRecovery(distributorId, { signal: request.signal })
    )
      .then((response) => {
        if (!request.isCurrent()) return;
        setState(read(response, distributorId));
        setLoadedVersion(version);
        setUncertain(false);
        saving.current = false;
      })
      .catch(() => {
        if (request.isCurrent())
          setError('Funding review status could not load. Reload before continuing.');
      })
      .finally(() => {
        if (request.isCurrent()) setBusy(false);
      });
  }, [begin, version, distributorId, allowed, retry]);

  const recover = async (event) => {
    event.preventDefault();
    if (
      !adminAccess() ||
      disabled ||
      busy ||
      uncertain ||
      saving.current ||
      !state?.available ||
      state.distributorId !== distributorId ||
      loadedVersion !== version
    )
      return;
    const previous = pending.current;
    if (previous && previous.expectedPostingRevision !== state.postingRevision) return;
    const text = reason.trim();
    if (
      !previous &&
      (text.length < 8 ||
        text.length > 500 ||
        Array.from(text).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127))
    ) {
      setError('Enter a review reason between 8 and 500 characters.');
      return;
    }
    const requestKey = previous?.requestKey || window.crypto?.randomUUID?.();
    if (!requestKey) {
      setError('A secure request reference is unavailable. Reload this page.');
      return;
    }
    const input = previous || {
      requestKey,
      expectedPostingRevision: state.postingRevision,
      reason: text,
    };
    const request = begin();
    pending.current = input;
    saving.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      readRecovery(await distributorService.recoverAdminCashFunding(distributorId, input), state);
      if (!request.isCurrent()) return;
      pending.current = null;
      setReason('');
      setNotice(
        'Fresh payment checks completed. Automatic funding review recovered. Balance unchanged.'
      );
      callbacks.current.onUpdated?.();
      setRetry((value) => value + 1);
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError('Recovery could not be confirmed. Reload current status before continuing.');
      }
    } finally {
      if (request.isCurrent()) {
        saving.current = false;
        setBusy(false);
      }
    }
  };
  if (!allowed) return null;
  const current =
    state?.distributorId === distributorId && loadedVersion === version ? state : null;
  const changed =
    pending.current &&
    current &&
    pending.current.expectedPostingRevision !== current.postingRevision;
  return (
    <fieldset className="me-cash me-cash-card">
      <legend>{t('Automatic funding review')}</legend>
      <div className="me-cash-heading">
        <h2>{t(current?.blocked ? 'Automatic funding restriction' : 'Payment review status')}</h2>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => setRetry((value) => value + 1)}
        >
          {t('Reload funding review')}
        </button>
      </div>
      <p>
        {t(
          'Recheck current payment observations before recovering a temporary automatic hold. This does not change balances, issue refunds or submit payments.'
        )}
      </p>
      {busy && <p role="status">{t('Checking funding review...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {current && (
        <p>
          {t(
            current.manualDisputePaused
              ? 'A manual dispute hold also applies. Manage it separately in Payment disputes.'
              : current.available
                ? 'Payment verification was temporarily unavailable. Fresh checks are required before recovery.'
                : current.blocked
                  ? 'The automatic restriction requires review and cannot be recovered here.'
                  : 'No automatic funding restriction is active.'
          )}
        </p>
      )}
      {changed && current.available && (
        <p role="alert">
          {t(
            'Funding status changed. Review the current restriction before another recovery request.'
          )}
        </p>
      )}
      {current?.available && (
        <form onSubmit={recover}>
          <fieldset disabled={disabled || busy || uncertain || Boolean(changed)}>
            <label>
              {t('Funding review reason')}
              <textarea
                value={reason}
                minLength={8}
                maxLength={500}
                required
                disabled={Boolean(pending.current)}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <p>{t('Use a brief review note. Do not enter card details or patient information.')}</p>
            <button type="submit" className="me-cash-primary">
              {t(
                pending.current ? 'Retry the same recovery request' : 'Recheck payments and recover'
              )}
            </button>
          </fieldset>
        </form>
      )}
    </fieldset>
  );
}
